import PropTypes from 'prop-types'
import { Table, Typography, Col, Space, Tooltip } from 'antd'
import { Link, generatePath } from 'react-router-dom'
import styled from 'styled-components'
import { colours } from '@qflow/theme'

import { Flag } from 'admin/lib/Icons/Flag'
import * as ROUTES from 'admin/routes'
import moment from 'moment'

export function AccountsList({ userAccountMappings }) {
  const formatTableData = () => {
    let formattedData = []

    if (userAccountMappings && userAccountMappings.length > 0) {
      formattedData = userAccountMappings
        .sort((a, b) =>
          a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })
        )
        .map(dataRow => {
          return {
            key: dataRow.accountId,
            accountId: dataRow.accountId,
            name: dataRow.name,
            createdAt: dataRow.createdAt,
            isEnabled: dataRow.isEnabled,
            projects: dataRow.projects.map(projectRow => {
              return {
                key: projectRow.projectId,
                projectId: projectRow.projectId,
                projectName: projectRow.name,
                country: projectRow.country,
                isProLicence: projectRow.isProLicence,
                isAlertsEnabled: projectRow.isAlertsEnabled,
                isClosedOut: projectRow.isClosedOut
              }
            })
          }
        })
    }

    return formattedData
  }

  const columns = [
    {
      title: 'Account Name',
      width: '30%',
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (_, account) => (
        <>
          <Link
            to={generatePath(ROUTES.ACCOUNTS.EDIT, {
              accountId: account.accountId
            })}
          >
            Edit Account Name
          </Link>
          <Col>
            <Typography.Text strong={true}>{account.name}</Typography.Text>
          </Col>
          <Col>
            <Typography.Text italic={true}>{account.accountId}</Typography.Text>
          </Col>
        </>
      )
    },
    {
      title: 'Projects',
      key: 'accountId',
      render: (_, account) => {
        const sortedProjects = account.projects.sort((a, b) =>
          a.projectName.localeCompare(b.projectName)
        )
        const topFiveProjects = sortedProjects.slice(0, 5)
        const remainingProjects = sortedProjects.slice(5)

        return (
          <Col>
            <Link
              to={generatePath(ROUTES.PROJECTS.LIST, {
                accountId: account.accountId
              })}
            >
              Manage Projects
            </Link>
            {topFiveProjects.map(project => (
              <div key={project.key}>
                <Flag
                  country={project.country}
                  style={{ width: '21px', margin: '0 5px 0 -26px' }}
                />
                <Space align="center">
                  <Typography.Text strong={true}>
                    {project.projectName}
                  </Typography.Text>
                  <Typography.Text italic={true}>
                    {project.projectId}
                  </Typography.Text>
                  {project.isClosedOut && <ClosedOut />}
                  {project.isProLicence && <Pro />}
                  {project.isAlertsEnabled && <Alerts />}
                </Space>
              </div>
            ))}
            {remainingProjects.length > 0 && (
              <details>
                <summary>
                  <Typography.Text type="secondary">See More</Typography.Text>
                </summary>

                {remainingProjects.map(project => (
                  <div key={project.key}>
                    <Space align="center">
                      <Typography.Text strong={true}>
                        {project.projectName}
                      </Typography.Text>
                      <Typography.Text italic={true}>
                        {project.projectId}
                      </Typography.Text>
                      {project.isClosedOut && <ClosedOut />}
                      {project.isProLicence && <Pro />}
                      {project.isAlertsEnabled && <Alerts />}
                    </Space>
                  </div>
                ))}
              </details>
            )}
          </Col>
        )
      }
    },
    {
      title: 'Creation Date',
      dataIndex: 'createdAt',
      width: '10rem',
      render: createdAt => {
        return (
          <Typography.Text>
            {moment(createdAt).local().format('DD/MM/YYYY HH:mm')}
          </Typography.Text>
        )
      }
    }
  ]

  columns.push({
    title: 'Integrations',
    key: 'edit',
    width: '7.5rem',

    render: accounts => (
      <>
        <Typography.Text
          style={{
            lineHeight: '1.25',
            margin: '0.5rem 0',
            display: 'block'
          }}
        >
          <Link
            to={generatePath(ROUTES.ONECLICK_LCA.EDIT, {
              accountId: accounts.accountId
            })}
          >
            One Click LCA
          </Link>
        </Typography.Text>
        <Typography.Text
          style={{
            lineHeight: '1.25',
            margin: '0.5rem 0',
            display: 'block'
          }}
        >
          <Link
            to={generatePath(ROUTES.PROCORE.EDIT, {
              accountId: accounts.accountId
            })}
          >
            Procore
          </Link>
        </Typography.Text>
      </>
    )
  })

  return (
    <Table
      columns={columns}
      dataSource={formatTableData()}
      pagination={false}
      style={{ width: '100%', margin: '3rem 1rem' }}
      rowClassName={'top-align'}
    />
  )
}

AccountsList.propTypes = {
  accounts: PropTypes.arrayOf(PropTypes.object)
}

function ClosedOut() {
  return (
    <Tooltip title="The Project is Closed Out and new tickets will not arrive. NOTE: Currently there is no prevention of new Connections being created.">
      <ClosedOutBadge strong={true}>Closed Out</ClosedOutBadge>
    </Tooltip>
  )
}

function Pro() {
  return <ProBadge strong={true}>Pro</ProBadge>
}

function Alerts() {
  return <AlertsBadge strong={true}>Alerts</AlertsBadge>
}

export const ProBadge = styled(Typography.Text)`
  display: inline-block;
  background-color: ${colours.GREEN};
  color: ${colours.WHITE};
  padding: 1px 4px;
  border-radius: 5px;
  margin: 0 0 1px;
`
export const AlertsBadge = styled(Typography.Text)`
  display: inline-block;
  min-width: 32px;
  background-color: ${colours.WHITE};
  color: ${colours.GREEN} !important;
  border: 1px solid ${colours.GREEN};
  padding: 1px 4px;
  border-radius: 5px;
  margin: 0 0 1px;
`
export const ClosedOutBadge = styled(Typography.Text)`
  display: inline-block;
  min-width: 32px;
  background-color: ${colours.WHITE};
  color: ${colours.RED} !important;
  border: 1px solid ${colours.RED};
  padding: 1px 4px;
  border-radius: 5px;
  margin: 0 0 1px;
`
